import { LEGACY_KEYS } from "@hachi/core";
import { b64url, deviceId, randomId } from "./device";
import { readJson, writeJson } from "./storage";
import type { KeyValue } from "./storage";

/**
 * Supabase Auth over plain fetch, storing the session exactly like the current app
 * (`hachi-garage-v1-session`), so people already signed in stay signed in after the switch.
 * Google sign-in uses PKCE; email sign-in uses a 6-digit code. No passwords.
 */

/** The saved session (`sessFrom` shape). */
export interface Session {
  access: string;
  refresh: string;
  /** Expiry, ms since epoch. */
  exp: number;
  uid: string;
  email: string;
  name: string;
  avatar: string;
}

interface Pkce {
  verifier: string;
  at: number;
  /** The hash route to return to after Google. */
  back: string;
}

export class AuthError extends Error {
  constructor(message: string, readonly status?: number) {
    super(message);
    this.name = "AuthError";
  }
}

export interface AuthConfig {
  supabaseUrl: string;
  supabaseKey: string;
}

export interface AuthDeps {
  store: KeyValue;
  fetch?: typeof fetch;
  now?: () => number;
}

interface TokenReply {
  access_token: string;
  refresh_token: string;
  expires_in?: number;
  user?: { id?: string; email?: string; user_metadata?: { full_name?: string; name?: string; avatar_url?: string } };
}

export function sessionFrom(j: TokenReply, now: number): Session {
  const u = j.user ?? {};
  const meta = u.user_metadata ?? {};
  return {
    access: j.access_token,
    refresh: j.refresh_token,
    exp: now + (j.expires_in || 3600) * 1000,
    uid: u.id ?? "",
    email: (u.email ?? "").toLowerCase(),
    name: meta.full_name || meta.name || "",
    avatar: meta.avatar_url || "",
  };
}

export type FinishResult = { session: Session; back: string } | { error: string; back: string } | null;

export function createAuth(cfg: AuthConfig, deps: AuthDeps) {
  const base = cfg.supabaseUrl.replace(/\/+$/, "");
  const doFetch = deps.fetch ?? ((...a: Parameters<typeof fetch>) => fetch(...a));
  const now = deps.now ?? Date.now;
  const { store } = deps;

  const getSession = () => readJson<Session>(store, LEGACY_KEYS.session);
  const setSession = (s: Session | null) => writeJson(store, LEGACY_KEYS.session, s);

  async function authFetch<T>(path: string, body: unknown): Promise<T> {
    const r = await doFetch(`${base}/auth/v1/${path}`, {
      method: "POST",
      headers: { apikey: cfg.supabaseKey, "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    const j = (await r.json().catch(() => ({}))) as Record<string, string>;
    if (!r.ok) throw new AuthError(j.error_description || j.msg || j.message || `auth ${r.status}`, r.status);
    return j as T;
  }

  function signedIn(j: TokenReply, fallbackEmail?: string): Session {
    const s = sessionFrom(j, now());
    if (!s.email && fallbackEmail) s.email = fallbackEmail;
    setSession(s);
    // Signing in again keeps this browser's device id: it stays the same registered device (D23).
    deviceId(store);
    return s;
  }

  let refreshing: Promise<string | null> | null = null;

  return {
    session: getSession,

    /** A valid access token, refreshed when under a minute is left. null = signed out. Network errors throw. */
    async accessToken(force = false): Promise<string | null> {
      const s = getSession();
      if (!s) return null;
      if (!force && s.exp - now() > 60_000) return s.access;
      refreshing ??= (async () => {
        try {
          const n = sessionFrom(await authFetch<TokenReply>("token?grant_type=refresh_token", { refresh_token: s.refresh }), now());
          setSession({ ...s, ...n });
          return n.access;
        } catch (e) {
          if (e instanceof AuthError && (e.status === 400 || e.status === 401 || e.status === 403)) {
            setSession(null);
            return null;
          }
          throw e;
        } finally {
          setTimeout(() => (refreshing = null), 0);
        }
      })();
      return refreshing;
    },

    /**
     * Saves the PKCE verifier and returns the Google sign-in address to open.
     * `returnTo` is the page Google comes back to (Supabase must allow it); `back` the hash route.
     */
    async googleUrl(returnTo: string, back: string): Promise<string> {
      const verifier = randomId(64);
      const challenge = b64url(new Uint8Array(await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier))));
      writeJson(store, LEGACY_KEYS.pkce, { verifier, at: now(), back } satisfies Pkce);
      return `${base}/auth/v1/authorize?provider=google&prompt=select_account&code_challenge_method=s256`
        + `&code_challenge=${encodeURIComponent(challenge)}&redirect_to=${encodeURIComponent(returnTo)}`;
    },

    /** Finishes Google sign-in from the return address's query (`?code=` or `?error=`); null when not returning from Google. */
    async finishGoogle(search: string): Promise<FinishResult> {
      const qs = new URLSearchParams(search);
      const code = qs.get("code"), err = qs.get("error_description") || qs.get("error");
      if (!code && !err) return null;
      const p = readJson<Pkce>(store, LEGACY_KEYS.pkce);
      writeJson(store, LEGACY_KEYS.pkce, null);
      const back = p?.back ?? "";
      if (err) return { error: err, back };
      if (!p?.verifier) return { error: "The sign-in expired. Please try again.", back };
      try {
        const j = await authFetch<TokenReply>("token?grant_type=pkce", { auth_code: code, code_verifier: p.verifier });
        return { session: signedIn(j), back };
      } catch (e) {
        return {
          error: e instanceof AuthError ? "Google sign-in didn't finish. Please try again." : "Couldn't connect. Check your internet and try again.",
          back,
        };
      }
    },

    /** Emails a 6-digit sign-in code. */
    async sendCode(email: string): Promise<void> {
      await authFetch("otp", { email, create_user: true });
    },

    /** Signs in with the emailed code. */
    async verifyCode(email: string, token: string): Promise<Session> {
      return signedIn(await authFetch<TokenReply>("verify", { type: "email", email, token }), email);
    },

    /** Signs out this browser only. */
    async signOut(): Promise<void> {
      const s = getSession();
      if (s) {
        try {
          await doFetch(`${base}/auth/v1/logout?scope=local`, {
            method: "POST",
            headers: { apikey: cfg.supabaseKey, Authorization: `Bearer ${s.access}` },
          });
        } catch {
          // Offline: the local sign-out still happens.
        }
      }
      setSession(null);
    },
  };
}

export type Auth = ReturnType<typeof createAuth>;

export const isEmail = (e: string) => /^[^@\s]+@[^@\s]+\.[a-z]{2,}$/i.test(e);

/** Plain-words message for an email-code error (`otpMsg`). */
export function codeErrorMessage(e: unknown): string {
  const status = e instanceof AuthError ? e.status : undefined;
  const m = String((e as { message?: unknown } | null)?.message ?? "");
  if (status === 429 || /security purposes|rate limit|too many/i.test(m)) return "Too many tries. Wait a minute, then try again.";
  if (/expired|invalid/i.test(m)) return "That code is wrong or expired. Check it, or send a new one.";
  if (/invalid format|validate email/i.test(m)) return "Enter a valid email address.";
  if (status) return "Couldn't send the code. Please try again, or continue with Google.";
  return "Couldn't connect. Check your internet and try again.";
}
